import { Link } from "react-router";
import { Button } from "flowbite-react";
import { ExternalLink } from "lucide-react";
import { YEATIES_PRIVACY_LINK_LABEL, YEATIES_PRIVACY_PATH } from "@/features/legal/config/yeatiesPrivacy.config";
import {
  YEATIES_TERMS_DATA_NOTICE,
  YEATIES_TERMS_LINK_LABEL,
  YEATIES_TERMS_PATH,
  YEATIES_TERMS_PERSONALISATION_NOTICE,
  YEATIES_TERMS_REVERSIBILITY,
} from "@/features/legal/config/yeatiesTerms.config";
import { YEATIES_CONSENT_NEW_TAB_HINT } from "@/features/purchasePoints/config/purchasePoints.config";
import { ResponsiveDialog } from "@/shared/components/responsiveDialog";
import { P } from "@/shared/ui/typographies/paragraph";

type YeatiesTermsDialogProps = {
  open: boolean
  onDecline: () => void
  onAccept: () => void
  isPending: boolean
  // No way out but an answer, for a question asked to someone who did not open it.
  isBlocking?: boolean
  // Tells a refusal in flight from an acceptance in flight, so only the latter reads as an activation.
  isDeclining?: boolean
};

export const YEATIES_TERMS_DIALOG_TITLE = "Activer Mes Yeaties";
export const YEATIES_TERMS_DIALOG_ENGAGEMENT = "Vos achats réglés avec votre carte Yeati chez une enseigne partenaire vous rapporteront des yeaties, convertibles en chèque pour la boutique Yeati.";
export const YEATIES_TERMS_DIALOG_DECLINE_LABEL = "Refuser";
export const YEATIES_TERMS_DIALOG_ACCEPT_LABEL = "Accepter et activer";
export const YEATIES_TERMS_DIALOG_PENDING_LABEL = "Activation en cours…";
export const YEATIES_TERMS_DIALOG_DECLINE_PENDING_LABEL = "Enregistrement…";

const LEGAL_LINK_CLASS = "inline-flex items-center gap-1 text-darkprimary underline underline-offset-4 text-sm dark:text-primary";
const LOCKED_BUTTON_CLASS = "aria-disabled:cursor-not-allowed aria-disabled:opacity-50";
const EXTERNAL_LINK_ICON_SIZE = 16;

type LegalLinkProps = { to: string, label: string, testId: string };

// Another tab: reading the terms in this one would unmount the row and its
// dialog, losing the answer he was about to give -- and, mid-request, the
// redirection its success carries.
const LegalLink = ({ to, label, testId }: LegalLinkProps) => (
  <Link to={to} target="_blank" rel="noopener noreferrer" className={LEGAL_LINK_CLASS} data-testid={testId}>
    {label}
    <ExternalLink size={EXTERNAL_LINK_ICON_SIZE} aria-hidden="true" />
    <span className="sr-only">{YEATIES_CONSENT_NEW_TAB_HINT}</span>
  </Link>
);

const getPendingLabel = (isPending: boolean, isDeclining: boolean) => {
  if (!isPending) return "";
  return isDeclining ? YEATIES_TERMS_DIALOG_DECLINE_PENDING_LABEL : YEATIES_TERMS_DIALOG_PENDING_LABEL;
};

/**
 * Asked on the way in; the way out has its own dialog, YeatiesOptOutDialog.
 *
 * The dialog closes on success alone: an acceptance the server refused must not
 * leave the switch showing a state it never confirmed.
 */
export const YeatiesTermsDialog = ({
  open, onDecline, onAccept, isPending, isBlocking = false, isDeclining = false,
}: YeatiesTermsDialogProps) => {
  const pendingLabel = getPendingLabel(isPending, isDeclining);

  return (
    <ResponsiveDialog
      open={open}
      onClose={onDecline}
      title={YEATIES_TERMS_DIALOG_TITLE}
      // onClose is the decline: leaving through Escape or the overlay would store a refusal.
      dismissible={!isBlocking && !isPending}
      // Accept last on both layouts: nearest the thumb on a phone, rightmost on a
      // desktop, and in the same order for the keyboard.
      footer={(
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end" aria-busy={isPending}>
          {/* aria-disabled, not disabled: a disabled button drops the focus out of the dialog. */}
          <Button
            pill
            color="light"
            className={LOCKED_BUTTON_CLASS}
            onClick={isPending ? undefined : onDecline}
            aria-disabled={isPending}
            data-testid="yeaties-terms-dialog-decline"
          >
            {isPending && isDeclining ? pendingLabel : YEATIES_TERMS_DIALOG_DECLINE_LABEL}
          </Button>
          <Button
            pill
            color="primary"
            className={LOCKED_BUTTON_CLASS}
            onClick={isPending ? undefined : onAccept}
            aria-disabled={isPending}
            data-testid="yeaties-terms-dialog-accept"
          >
            {isPending && !isDeclining ? pendingLabel : YEATIES_TERMS_DIALOG_ACCEPT_LABEL}
          </Button>
          <span className="sr-only" role="status">{pendingLabel}</span>
        </div>
      )}
    >
      <div className="flex flex-col gap-5" data-testid="yeaties-terms-dialog">
        <P size="sm">{YEATIES_TERMS_DIALOG_ENGAGEMENT}</P>
        <P size="sm">{YEATIES_TERMS_DATA_NOTICE}</P>
        <P size="sm">{YEATIES_TERMS_PERSONALISATION_NOTICE}</P>
        <P size="sm">{YEATIES_TERMS_REVERSIBILITY}</P>

        <div className="flex flex-col gap-2">
          <LegalLink to={YEATIES_TERMS_PATH} label={YEATIES_TERMS_LINK_LABEL} testId="yeaties-terms-dialog-link" />
          <LegalLink to={YEATIES_PRIVACY_PATH} label={YEATIES_PRIVACY_LINK_LABEL} testId="yeaties-privacy-dialog-link" />
        </div>
      </div>
    </ResponsiveDialog>
  );
};

YeatiesTermsDialog.displayName = "YeatiesTermsDialog";
